<template>
    <modal size="lg" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="iwo">Edit Income Withholding Order</h3>
            <h3 class="modal-title" v-else>Add Income Withholding Order</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                </div>
            </div>

            <div v-if="step == 'info'">
                <div class="form-row">
                    <div class="col col-6">
                        <form-input v-model="formData.iwo_type" :errors="errors.iwo_type" type="select" :options="iwoTypeOptions" :disabled="iwo ? true : false" label="IWO Type"/>
                    </div>
                    <div class="col col-6">
                        <form-input v-model="formData.order_date" :errors="errors.order_date" type="date" label="Order Date"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="col col-6">
                        <form-input v-model="formData.state" :errors="errors.state" type="text" label="State/Tribe/Territory"/>
                    </div>
                    <div class="col col-6">
                        <form-input v-model="formData.remittance_id" :errors="errors.remittance_id" type="text" label="Remittance ID"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="col col-6">
                        <form-input v-model="formData.country" :errors="errors.country" type="text" label="Countr./Dist./Tribe"/>
                    </div>
                    <div class="col col-6">
                        <form-input v-model="formData.order_id" :errors="errors.order_id" type="text" label="Order ID"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="col col-6">
                        <form-input v-model="formData.entity" :errors="errors.entity" type="text" label="Private Individual/Entity"/>
                    </div>
                    <div class="col col-6">
                        <form-input v-model="formData.case_id" :errors="errors.case_id" type="text" label="Case ID"/>
                    </div>
                </div>
            </div>

            <div v-if="step == 'amounts'" class="order-info-step">
                <h5>Order Information</h5>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.current_child_support_amount" :errors="errors.current_child_support_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">current child support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.past_due_child_support_amount" :errors="errors.past_due_child_support_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false" />
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">past-due child support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3"></div>
                    <div class="col col-9 pb-2">
                        Arrears greater than 12 weeks?
                        <input type="radio" class="ml-2 mr-hf" name="arrears_gt_12_weeks" :checked="formData.arrears_gt_12_weeks" @click="formData.arrears_gt_12_weeks = true">Yes
                        <input type="radio" class="ml-2 mr-hf" name="arrears_gt_12_weeks" :checked="!formData.arrears_gt_12_weeks" @click="formData.arrears_gt_12_weeks = false">No
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.current_cash_medical_amount" :errors="errors.current_cash_medical_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">current cash medical support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.past_due_cash_medical_amount" :errors="errors.past_due_cash_medical_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">past-due medical support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.current_spousal_amount" :errors="errors.current_spousal_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">current spousal support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.past_due_spousal_amount" :errors="errors.past_due_spousal_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">past-due spousal support</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.other_amount" :errors="errors.other_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0">other</p>
                    </div>
                </div>
                <div class="form-row border-top py-1">
                    <div class="col col-3">
                        <form-input v-model="formData.total_amount" :errors="errors.total_amount" class="grid-input" type="currency" @input="showTotalAmountWarning = false"/>
                    </div>
                    <div class="col col-3 d-flex align-items-center">
                        <p class="mb-0">per month</p>
                    </div>
                    <div class="col col-6 d-flex align-items-center">
                        <p class="mb-0"><strong>Total Amount to Withhold</strong></p>
                    </div>
                </div>
                <alert-box type="caution" v-if="showTotalAmountWarning">
                    <strong>Warning:</strong> amounts do not add up to Total Amount to Withhhold. Are you sure want to continue?
                </alert-box>
            </div>

            <div v-if="step == 'remittance'">
                <h5>Remittance Information</h5>
                <form-input v-model="formData.remittance_name" :errors="errors.remittance_name" type="text" label="Remit Payment To"/>
                <form-input v-model="formData.remittance_address" :errors="errors.remittance_address" type="text" label="Address"/>
                <div class="form-row">
                    <div class="col-4">
                        <form-input label="City" v-model="formData.remittance_city" :errors="errors.remittance_city" type="text" :maxlength="50"/>
                    </div>
                    <div class="col-4">
                        <form-input label="State" v-model="formData.remittance_state" :errors="errors.remittance_state" type="select" :options="stateOptions"/>
                    </div>
                    <div class="col-4">
                        <form-input label="Zip Code" :maxlength="5" v-model="formData.remittance_postal_code" :errors="errors.remittance_postal_code" type="text" />
                    </div>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button v-if="step == steps[0]"type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="prevStep()">
                    Back
                </button>

                <button v-if="step == steps[steps.length-1]" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="nextStep()">
                    Continue
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import states from '@/states'
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    props: ['client', 'employee', 'iwo'],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const data = {
            formData: this.makeFormData(),
            formRulesByStep: {
                'info': {
                    iwo_type: {presence: {allowEmpty: false}},
                    order_date: {presence: {allowEmpty: false}},
                },
                'amounts': {
                    total_amount: {presence: {allowEmpty: false}},
                },
                'remittance': {}
            },
            formRules: {},
            formLabels: {
                iwo_type: 'IWO Type',
                order_date: 'Order Date',
                total_amount: 'Total Amount'
            },
            iwoTypes: [],
            steps: ['info', 'amounts', 'remittance'],
            step: 'info',
            showTotalAmountWarning: false
        }

        return data
    },
    mounted() {
        this.getIWOTypes()
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        iwoTypeOptions() {
            const result = []

            this.iwoTypes.forEach(i => {
                result.push({text: i.name, value: i.id})
            })

            result.sort((a, b) => {
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        stateOptions() {
            const result = []
            states.forEach((s) => {
                result.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return result
        },
    },
    methods: {
        makeFormData() {
            const data = {
                iwo_type: this.iwo ? this.iwo.iwo_type_id : '',
                order_date: this.iwo ? this.iwo.order_date : '',
                state: this.iwo ? this.iwo.state : '',
                country: this.iwo ? this.iwo.country : '',
                entity: this.iwo ? this.iwo.entity : '',
                remittance_id: this.iwo ? this.iwo.remittance_id : '',
                order_id: this.iwo ? this.iwo.order_id : '',
                case_id: this.iwo ? this.iwo.case_id : '',
                current_child_support_amount: this.iwo ? this.iwo.current_child_support_amount : '',
                past_due_child_support_amount: this.iwo ? this.iwo.past_due_child_support_amount : '',
                arrears_gt_12_weeks: this.iwo ? this.iwo.arrears_gt_12_weeks : false,
                current_cash_medical_amount: this.iwo ? this.iwo.current_cash_medical_amount : '',
                past_due_cash_medical_amount: this.iwo ? this.iwo.past_due_cash_medical_amount : '',
                current_spousal_amount: this.iwo ? this.iwo.current_spousal_amount : '',
                past_due_spousal_amount: this.iwo ? this.iwo.past_due_spousal_amount : '',
                other_amount: this.iwo ? this.iwo.other_amount : '',
                total_amount: this.iwo ? this.iwo.total_amount : '',
                amount_paid: this.iwo ? this.iwo.amount_paid : '',
                remittance_name: this.iwo ? this.iwo.remittance_name : '',
                remittance_address: this.iwo ? this.iwo.remittance_address : '',
                remittance_city: this.iwo ? this.iwo.remittance_city : '',
                remittance_state: this.iwo ? this.iwo.remittance_state : '',
                remittance_postal_code: this.iwo ? this.iwo.remittance_postal_code : '',
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRulesByStep[this.step]))
            return rules
        },
        validate() {
            let isValid = FormMixin.methods.validate.call(this)

            if (this.step == 'amounts' && !this.showTotalAmountWarning) {
                this.checkTotalAmount()
                if (this.showTotalAmountWarning) {
                    isValid = false
                }
            }

            return isValid
        },
        checkTotalAmount() {
            if (!this.formData.total_amount) {
                return
            }
            const fieldsToSum = [
                'current_child_support_amount',
                'past_due_child_support_amount',
                'current_cash_medical_amount',
                'past_due_cash_medical_amount',
                'current_spousal_amount',
                'past_due_spousal_amount',
                'other_amount'
            ]

            const sum = fieldsToSum.reduce((acc, field) => {
                const value = parseFloat(this.formData[field]) || 0
                return acc + value
            }, 0)

            const total = parseFloat(this.formData.total_amount) || 0

            const roundedSum = Math.round(sum * 100) / 100
            const roundedTotal = Math.round(total * 100) / 100

            if (roundedSum !== roundedTotal) {
                this.showTotalAmountWarning = true
            } else {
                this.showTotalAmountWarning = false
            }
        },
        getIWOTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/admin/tax-engine/involuntary-withholding-types`).then(resp => {
                this.iwoTypes = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
                this.$store.dispatch('STOP_LOADING')
            })
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            let url
            if (this.iwo) {
                url = `/clients/${this.client.id}/employees/${this.employee.id}/iwos/${this.iwo.id}`
            } else {
                url = `/clients/${this.client.id}/employees/${this.employee.id}/iwos`
            }
            this.$store.dispatch('START_LOADING')
            return this.$api.post(url, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved order.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        },
        nextStep() {
            if (this.validate()) {
                this.step = this.steps[this.steps.indexOf(this.step)+1]
            }
        },
        prevStep() {
            this.step = this.steps[this.steps.indexOf(this.step)-1]
        }
    }
}
</script>

<style lang="scss">
.order-info-step {
    .grid-input {
        .mui-textfield {
            margin-bottom: 0px;
        }
    }
}
</style>
